import { useSyncExternalStore } from "react";
// Sprint: Product Analytics V1 — currency_changed/language_changed were
// never instrumented anywhere. _setCurrency/_setLanguage below are the
// SAME single module-level choke point every consumer's setCurrency()/
// setLanguage() already funnels through, so this is one call site for
// every surface (header selector, /cuenta site preferences, ...).
import { logMarketplaceEvent } from "@/lib/marketplaceEventLog";

const STORAGE_KEY = "rhea_prefs_v1";

const ALLOWED_CURRENCIES = ["USD", "EUR", "COP", "BRL"] as const;
const ALLOWED_LANGUAGES  = ["es", "en", "pt"]           as const;

type Currency = typeof ALLOWED_CURRENCIES[number];
export type Language = typeof ALLOWED_LANGUAGES[number];

interface Prefs { currency: Currency; language: Language; }

const DEFAULTS: Prefs = { currency: "USD", language: "es" };

function validCurrency(v: unknown): Currency {
  return ALLOWED_CURRENCIES.includes(v as Currency) ? (v as Currency) : DEFAULTS.currency;
}
function validLanguage(v: unknown): Language {
  return ALLOWED_LANGUAGES.includes(v as Language) ? (v as Language) : DEFAULTS.language;
}

function loadPrefs(): Prefs {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULTS;
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    return {
      currency: validCurrency(parsed.currency),
      language: validLanguage(parsed.language),
    };
  } catch {
    return DEFAULTS;
  }
}

function savePrefs(prefs: Prefs): void {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs)); } catch { /* storage unavailable */ }
}

// ── Semantic Intent Filters + Reference Currency Display V1.1, Parte F ──
// usePreferences() used to back each caller with its OWN independent
// useState, synced to localStorage only across a remount — so changing
// currency in the header never updated an already-mounted marketplace
// card until the whole page reloaded. This module-level external store +
// useSyncExternalStore (React 19, no new dependency) makes every
// usePreferences() call site share ONE reactive source of truth: calling
// setCurrency() anywhere re-renders every other mounted consumer
// immediately, with no page/marketplace refetch (Task 4's own
// requirement — this hook never touches network state, only local
// display preference). Public API (the returned shape and every
// exported name) is unchanged, so no existing call site needs updating.
let _currentPrefs: Prefs = loadPrefs();
const _listeners = new Set<() => void>();

function _emit(): void {
  for (const listener of _listeners) listener();
}

function _subscribe(listener: () => void): () => void {
  _listeners.add(listener);
  return () => { _listeners.delete(listener); };
}

function _getSnapshot(): Prefs {
  return _currentPrefs;
}

function _setCurrency(currency: string): void {
  const safe = validCurrency(currency);
  if (safe === _currentPrefs.currency) return;
  _currentPrefs = { ..._currentPrefs, currency: safe };
  savePrefs(_currentPrefs);
  _emit();
  // Sprint: Product Analytics V1 — only fires on a REAL change (the
  // early-return above already guards a no-op set), and only the
  // already-validated `safe` value, never the raw input.
  logMarketplaceEvent("currency_changed", { currency: safe });
}

function _setLanguage(language: string): void {
  const safe = validLanguage(language);
  if (safe === _currentPrefs.language) return;
  _currentPrefs = { ..._currentPrefs, language: safe };
  savePrefs(_currentPrefs);
  _emit();
  logMarketplaceEvent("language_changed", { language: safe });
}

export function usePreferences() {
  const prefs = useSyncExternalStore(_subscribe, _getSnapshot, _getSnapshot);
  return { currency: prefs.currency, language: prefs.language, setCurrency: _setCurrency, setLanguage: _setLanguage };
}

// ─── Hotfix/Sprint: Language Selector Marketplace Wiring V1 ────────────────
// The ONE place the header's 2-letter `language` preference becomes the
// backend's market_locale — a real BCP-47-ish tag ("es-CO"|"en-US"|"pt-BR")
// used for Viator locale headers, query-intent parsing, and filter-taxonomy
// chip labels (see engine/supply_integration/marketplace_filter_taxonomy.py's
// own group_label()). A plain lookup table, not a hook — every marketplace
// search-request builder call site needs this as a pure value, not a second
// live subscription to the preferences store.
const MARKET_LOCALE_BY_LANGUAGE: Record<Language, string> = {
  es: "es-CO",
  en: "en-US",
  pt: "pt-BR",
};

export function languageToMarketLocale(language: Language): string {
  return MARKET_LOCALE_BY_LANGUAGE[language] ?? MARKET_LOCALE_BY_LANGUAGE[DEFAULTS.language];
}
